import { useEffect, useState } from "react";
import { Activity, CircleDashed, WifiOff } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusStyles = { connected: "bg-emerald-500", degraded: "bg-amber-500", configured: "bg-blue-500", disconnected: "bg-zinc-400", error: "bg-red-500" };
const statusLabels = { connected: "SAP Live", degraded: "Degraded", configured: "Not tested", disconnected: "Disconnected", error: "Status error" };

const ConnectionStatus = () => {
  const [status, setStatus] = useState("disconnected");
  const [detail, setDetail] = useState("No active SAP system");

  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      try {
        const connections = (await axios.get(`${API}/sap-connections`)).data;
        const active = connections.find((item) => item.is_active);
        if (!active) { if (mounted) { setStatus("disconnected"); setDetail("No active SAP system"); } return; }
        try {
          const response = await axios.get(`${API}/sap-connections/${active.id}/endpoints`);
          const endpoints = Array.isArray(response.data?.endpoints) ? response.data.endpoints : [];
          const tested = endpoints.filter((item) => item.status && item.status !== "not_tested");
          const healthy = endpoints.filter((item) => item.status === "healthy").length;
          const unreachable = endpoints.filter((item) => ["unreachable", "timeout"].includes(item.status)).length;
          if (!mounted) return;
          if (!tested.length) { setStatus("configured"); setDetail(`${active.name} is active but endpoints have not been tested`); }
          else if (healthy > 0 && healthy < endpoints.length) { setStatus("degraded"); setDetail(`${active.name}: ${healthy}/${endpoints.length} endpoints healthy`); }
          else if (healthy > 0) { setStatus("connected"); setDetail(`${active.name}: ${healthy}/${endpoints.length} endpoints healthy`); }
          else if (unreachable > 0) { setStatus("disconnected"); setDetail(`${active.name} is active but SAP is unreachable`); }
          else { setStatus("degraded"); setDetail(`${active.name} is active but no endpoint is healthy`); }
        } catch { if (mounted) { setStatus("configured"); setDetail(`${active.name} is active; endpoint health is unavailable`); } }
      } catch { if (mounted) { setStatus("error"); setDetail("Unable to load SAP connection status"); } }
    };
    fetchStatus(); const interval = setInterval(fetchStatus, 30000); return () => { mounted = false; clearInterval(interval); };
  }, []);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex cursor-pointer items-center gap-2 rounded-full border border-slate-200/80 bg-slate-100/70 px-2.5 py-1 transition-all hover:bg-slate-200/60" data-testid="connection-status">
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${statusStyles[status]}`} />
              <span className={`relative inline-flex h-2 w-2 rounded-full ${statusStyles[status]}`} />
            </span>
            <span className="hidden text-xs font-semibold text-slate-700 md:inline">{statusLabels[status]}</span>
            {status === "disconnected" || status === "error" ? <WifiOff className="h-3.5 w-3.5 text-muted-foreground md:hidden" /> : status === "configured" ? <CircleDashed className="h-3.5 w-3.5 text-blue-500 md:hidden" /> : <Activity className={`h-3.5 w-3.5 md:hidden ${status === "connected" ? "text-emerald-500" : "text-amber-500"}`} />}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs font-medium">{detail}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
export default ConnectionStatus;

